import Layout from "../../../component/layout/Layout";
import KPI from "../components/KPI";
import MaskedTable from "../components/MaskedTable";
import DashboardPagination from "../components/DashboardPagination";
import Chart from "../components/Chart";
import ExcelDownloadModal from "../components/ExcelDownloadModal";
import type { NaverMember } from "../data/users";
import { useState, useEffect, useCallback } from "react";
import * as apiUtil from "../../../util/apiUtil";

interface JoinStat {
  joinDate: string;
  cnt: number;
}

interface KpiData {
  totalUsers: number;
  todayUsers: number;
  yesterdayUsers: number;
  weekUsers: number;
  prevWeekUsers: number;
  joinStats: JoinStat[];
}

export default function Dashboard() {
  return (
    <Layout>
      <DashboardContent />
    </Layout>
  );
}

function DashboardContent() {
  const [members, setMembers] = useState<NaverMember[]>([]);
  const [kpi, setKpi] = useState<KpiData>({
    totalUsers: 0,
    todayUsers: 0,
    yesterdayUsers: 0,
    weekUsers: 0,
    prevWeekUsers: 0,
    joinStats: [],
  });
  const [lastWithdrawalMailSentAt, setLastWithdrawalMailSentAt] = useState<string | null>(null);
  const [lastExcelDownloadedAt, setLastExcelDownloadedAt] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [range, setRange] = useState(7);
  const [showExcelModal, setShowExcelModal] = useState(false);

  const PAGE_SIZE = 10;

  const fetchMembers = useCallback((sort: string, pageNum: number) => {
    apiUtil.asyncRequest({
      url: `/api/bizchat/naver-app/members?sort=${sort}&page=${pageNum}&size=${PAGE_SIZE}`,
      method: apiUtil.requestMethod.GET,
      success: (result: any) => {
        setMembers(Array.isArray(result?.members) ? result.members : []);
        setTotalPages(result?.totalPages ?? 0);
        setLastWithdrawalMailSentAt(result?.lastWithdrawalMailSentAt ?? null);
        setLastExcelDownloadedAt(result?.lastExcelDownloadedAt ?? null);
      },
    });
  }, []);

  const fetchKpi = useCallback((days: number) => {
    apiUtil.asyncRequest({
      url: `/api/bizchat/naver-app/kpi?days=${days}`,
      method: apiUtil.requestMethod.GET,
      success: (result: any) => {
        setKpi({
          totalUsers: result?.totalUsers ?? 0,
          todayUsers: result?.todayUsers ?? 0,
          yesterdayUsers: result?.yesterdayUsers ?? 0,
          weekUsers: result?.weekUsers ?? 0,
          prevWeekUsers: result?.prevWeekUsers ?? 0,
          joinStats: Array.isArray(result?.joinStats) ? result.joinStats : [],
        });
      },
    });
  }, []);

  useEffect(() => {
    // fetchMembers(sortOrder, page);
    fetchKpi(range);
  }, []);

  useEffect(() => {
    fetchKpi(range);
  }, [range]);

  const showWithdrawalBanner = lastWithdrawalMailSentAt !== null && (
    lastExcelDownloadedAt === null ||
    new Date(lastWithdrawalMailSentAt) > new Date(lastExcelDownloadedAt)
  );

  function calcRate(current: number, prev: number) {
    if (prev === 0) return current > 0 ? 100 : 0;
    return ((current - prev) / prev) * 100;
  }

  const todayRate = calcRate(kpi.todayUsers, kpi.yesterdayUsers);
  const weekRate = calcRate(kpi.weekUsers, kpi.prevWeekUsers);

  function handleSortToggle() {
    const next = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(next);
    setPage(1);
    fetchMembers(next, 1);
  }

  function handlePageChange(p: number) {
    setPage(p);
    fetchMembers(sortOrder, p);
  }

  const chartData = kpi.joinStats.map((s) => ({
    date: new Date(s.joinDate).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" }),
    count: s.cnt,
  }));

  function handleDownload(reason: string) {
    setShowExcelModal(false);
    apiUtil.fileDownloadRequest({
      url: "/api/bizchat/naver-app/members/excel",
      method: apiUtil.requestMethod.POST,
      data: { reason },
      success: (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "naver-members.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setLastExcelDownloadedAt(new Date().toISOString());
      },
    });
  }

  return (
    <>
    {/* showExcelModal && (
      <ExcelDownloadModal
        onConfirm={handleDownload}
        onClose={() => setShowExcelModal(false)}
      />
    ) */}
    <div className="tw-max-w-7xl tw-mx-auto tw-px-6 tw-py-8 tw-space-y-12">

      <div>
        <h1 className="tw-text-3xl tw-font-bold tw-text-slate-900">
          사용자 대시보드
        </h1>
        <p className="tw-text-slate-500 tw-mt-2">
          서비스 가입자 현황 관리
        </p>
      </div>

      {/* 탈퇴 알림 배너 */}
      {/*{showWithdrawalBanner && (*/}
      {/*  <div className="tw-flex tw-items-start tw-gap-3 tw-bg-amber-50 tw-border tw-border-amber-300 tw-rounded-xl tw-px-5 tw-py-4">*/}
      {/*    <span className="tw-text-amber-500 tw-text-xl tw-leading-none">⚠️</span>*/}
      {/*    <div>*/}
      {/*      <p className="tw-text-amber-800 tw-font-semibold tw-text-sm">*/}
      {/*        탈퇴한 회원이 있습니다.*/}
      {/*      </p>*/}
      {/*      <p className="tw-text-amber-700 tw-text-xs tw-mt-0.5">*/}
      {/*        최신 데이터를 반영하려면 엑셀을 새로 다운로드하세요.*/}
      {/*      </p>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*)}*/}

      {/* KPI */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6">
        <KPI title="총 가입자" value={kpi.totalUsers} rate={0} useRate={false} />
        <KPI title="오늘 가입" value={kpi.todayUsers} rate={todayRate} useRate={true} />
        <KPI title="최근 7일 가입" value={kpi.weekUsers} rate={weekRate} useRate={true} />
      </div>

      {/* Chart */}
      <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-6">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <h2 className="tw-text-lg tw-font-semibold tw-text-slate-800">
            가입자 증가 추이
          </h2>
          <select
            value={range}
            onChange={(e) => setRange(Number(e.target.value))}
            className="tw-border tw-border-slate-200 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
          >
            <option value={7}>최근 7일</option>
            <option value={30}>최근 30일</option>
          </select>
        </div>
        <Chart data={chartData} />
      </div>

      {/* 리스트 - 임시 비활성화
      <div className="tw-space-y-6">
        <div className="tw-flex tw-justify-end">
          <button
            onClick={() => setShowExcelModal(true)}
            className="tw-bg-[#5D87FF] tw-text-white tw-px-6 tw-py-3 tw-rounded-xl tw-text-sm tw-font-semibold tw-shadow-lg hover:tw-bg-blue-600 tw-border-0"
          >
            엑셀 다운로드
          </button>
        </div>

        <MaskedTable users={members} sortOrder={sortOrder} onSortToggle={handleSortToggle} />

        <DashboardPagination
          page={page}
          total={totalPages}
          setPage={handlePageChange}
        />
      </div>
      */}
    </div>
    </>
  );
}
